import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { AccountBalance } from '../../models/report.model';
import { AccountingAccount } from '../../models/accounting.account.model';
import { formatMoney, amountClass, tabularStyle } from '../../scripts/format.value.script';

export interface ReportTreeProps {
  groups: AccountBalance[];
  groupsPrevious?: AccountBalance[];
  locale: string;
  valueKey?: 'balance' | 'total_debits' | 'total_credits';
  showDebitCredit?: boolean;
  showZero?: boolean;
  accounts?: AccountingAccount[] | null;
  previousLabel?: string;
}

function findByCode(nodes: AccountBalance[], code: string): AccountBalance | null {
  for (const node of nodes) {
    if (node.code === code) return node
    if (node.children) {
      const found = findByCode(node.children, code)
      if (found) return found
    }
  }
  return null
}

function localized(name: { fr: string; en: string; de: string; it: string }, locale: string): string {
  return (name as Record<string, string>)[locale.toLowerCase()] ?? name.en ?? name.fr ?? ''
}

function weightClass(depth: number): string {
  if (depth <= 0) return 'fw-bolder'
  if (depth === 1) return 'fw-bold'
  if (depth === 2) return 'fw-semibold'
  return 'fw-normal text-gray-700'
}

function variationText(current: number, previous: number | null): string {
  if (previous === null || previous === undefined) return ''
  const diff = current - previous
  return (diff >= 0 ? '+' : '') + formatMoney(diff)
}

function variationDiff(current: number, previous: number | null): number | null {
  if (previous === null || previous === undefined) return null
  return current - previous
}

function isNonZero(n: number | null | undefined): boolean {
  return typeof n === 'number' && Math.abs(n) > 0.005
}

function filterZeroTree(
  nodes: AccountBalance[],
  valueKey: 'balance' | 'total_debits' | 'total_credits',
  previousTree: AccountBalance[] | undefined
): AccountBalance[] {
  const result: AccountBalance[] = []
  for (const node of nodes) {
    const filteredChildren = node.children
      ? filterZeroTree(node.children, valueKey, previousTree)
      : undefined

    const selfNonZero = isNonZero(node[valueKey])
    let prevNonZero = false
    if (previousTree) {
      const prev = findByCode(previousTree, node.code)
      prevNonZero = !!prev && isNonZero(prev[valueKey])
    }
    const hasKeptChildren = !!filteredChildren && filteredChildren.length > 0

    if (selfNonZero || prevNonZero || hasKeptChildren) {
      result.push({
        ...node,
        children: hasKeptChildren ? filteredChildren : (node.children && node.children.length > 0 ? [] : undefined)
      })
    }
  }
  return result
}

interface RowProps {
  node: AccountBalance;
  previousTree?: AccountBalance[];
  locale: string;
  valueKey: 'balance' | 'total_debits' | 'total_credits';
  showDebitCredit: boolean;
  hasPrevious: boolean;
  depth: number;
  accountIdByCode?: Map<string, string>;
}

const ReportRow: React.FunctionComponent<RowProps> = ({ node, previousTree, locale, valueKey, showDebitCredit, hasPrevious, depth, accountIdByCode }) => {
  const hasChildren = node.children && node.children.length > 0
  const value = node[valueKey]
  const prevNode = previousTree ? findByCode(previousTree, node.code) : null
  const prevValue = prevNode ? prevNode[valueKey] : null
  const diff = variationDiff(value, prevValue)

  const paddingLeft = 8 + depth * 24
  const accountId = node.type === 'leave' && accountIdByCode ? accountIdByCode.get(node.code) : undefined
  const label = localized(node.name, locale)

  return (
    <>
      <tr className={weightClass(depth)}>
        <td style={{ paddingLeft }}>
          <span className="text-muted me-2">{node.code}</span>
          { accountId
            ? <Link to={"/main/accounting/plan/view/" + accountId} className="text-dark fw-bolder text-hover-primary">{label}</Link>
            : label
          }
        </td>
        { showDebitCredit &&
          <>
            <td className={`text-end text-nowrap ${amountClass(node.total_debits)}`} style={tabularStyle}>{formatMoney(node.total_debits)}</td>
            <td className={`text-end text-nowrap ${amountClass(node.total_credits)}`} style={tabularStyle}>{formatMoney(node.total_credits)}</td>
          </>
        }
        <td className={`text-end text-nowrap ${amountClass(value)}`} style={tabularStyle}>{formatMoney(value)}</td>
        { hasPrevious &&
          <>
            <td className={`text-end text-nowrap ${amountClass(prevValue)}`} style={tabularStyle}>{prevValue !== null ? formatMoney(prevValue) : ''}</td>
            <td className={`text-end text-nowrap ${amountClass(diff)}`} style={tabularStyle}>{variationText(value, prevValue)}</td>
          </>
        }
      </tr>
      { hasChildren && node.children!.map((child) => (
        <ReportRow
          key={child.code}
          node={child}
          previousTree={previousTree}
          locale={locale}
          valueKey={valueKey}
          showDebitCredit={showDebitCredit}
          hasPrevious={hasPrevious}
          depth={depth + 1}
          accountIdByCode={accountIdByCode}
        />
      ))}
    </>
  )
}

const AppReportTree: React.FunctionComponent<ReportTreeProps> = ({ groups, groupsPrevious, locale, valueKey = 'balance', showDebitCredit = false, showZero = false, accounts, previousLabel }) => {
  const {lang} = StateLanguage()
  const hasPrevious = !!groupsPrevious && groupsPrevious.length > 0
  const displayedGroups = showZero ? groups : filterZeroTree(groups, valueKey, groupsPrevious)

  const accountIdByCode = useMemo(() => {
    if (!accounts) return undefined
    const map = new Map<string, string>()
    const walk = (list: AccountingAccount[]) => {
      for (const a of list) {
        if (a.code && a.id) map.set(a.code, a.id)
        if (a.children) walk(a.children)
      }
    }
    walk(accounts)
    return map
  }, [accounts])

  return (
    <div className="table-responsive">
      <table className="table align-middle table-row-dashed gy-2 mb-0">
        <thead className="border-bottom">
          <tr className="text-muted text-uppercase fw-bolder fs-7">
            <th className="min-w-250px text-start">{lang.labels.account}</th>
            { showDebitCredit &&
              <>
                <th className="min-w-100px text-end">{lang.labels.debit}</th>
                <th className="min-w-100px text-end">{lang.labels.credit}</th>
              </>
            }
            <th className="min-w-120px text-end">{lang.labels.balance}</th>
            { hasPrevious &&
              <>
                <th className="min-w-120px text-end">{previousLabel || lang.labels.previousYear}</th>
                <th className="min-w-100px text-end">{lang.labels.variation}</th>
              </>
            }
          </tr>
        </thead>
        <tbody className="text-secondary">
          { displayedGroups.map((group) => (
            <ReportRow
              key={group.code}
              node={group}
              previousTree={groupsPrevious}
              locale={locale}
              valueKey={valueKey}
              showDebitCredit={showDebitCredit}
              hasPrevious={hasPrevious}
              depth={0}
              accountIdByCode={accountIdByCode}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default AppReportTree;
